import { useState, useEffect, useCallback } from 'react';
import { Layers, Search, Plus, RefreshCw, Edit2, Trash2, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { taxApi } from '@/api/tax.api';
import type { TaxGroup, TaxGroupComponent, TaxCode } from '@/api/tax.api';
import { usePermission } from '@/hooks/usePermission';
import { Permissions } from '@/constants/permissions';
import { Spinner } from '@/components/ui/Spinner';
import { Combobox } from '@/components/ui/Combobox';
import { cn } from '@/utils/cn';

// ── Group Dialog ───────────────────────────────────────────────────────────────

interface GroupDialogProps {
  open: boolean;
  record: TaxGroup | null;
  availableCodes: TaxCode[];
  onClose: () => void;
  onSaved: () => void;
}

const EMPTY_GROUP: Partial<TaxGroup> = {
  groupCode: '', groupName: '', isActive: true, remarks: null, components: [],
};

const GroupDialog = ({ open, record, availableCodes, onClose, onSaved }: GroupDialogProps) => {
  const [form, setForm] = useState<Partial<TaxGroup>>(EMPTY_GROUP);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [addCodeId, setAddCodeId] = useState('');

  useEffect(() => {
    setForm(record ? { ...record, components: [...(record.components ?? [])] } : { ...EMPTY_GROUP, components: [] });
    setError(null); setAddCodeId('');
  }, [record, open]);

  if (!open) return null;

  const set = (k: keyof TaxGroup, v: unknown) => setForm(f => ({ ...f, [k]: v }));

  const addComponent = () => {
    if (!addCodeId) return;
    const code = availableCodes.find(c => c.id === addCodeId);
    if (!code) return;
    const existing = (form.components ?? []).find(c => c.taxCodeId === addCodeId);
    if (existing) return;
    const seq = (form.components ?? []).length + 1;
    const newComp: TaxGroupComponent = {
      taxCodeId: code.id, code: code.code, name: code.name,
      taxRate: code.taxRate, sequence: seq, isOnBase: true,
    };
    set('components', [...(form.components ?? []), newComp]);
    setAddCodeId('');
  };

  const updateComponent = (idx: number, k: keyof TaxGroupComponent, v: unknown) => {
    const comps = [...(form.components ?? [])];
    comps[idx] = { ...comps[idx], [k]: v };
    set('components', comps);
  };

  const removeComponent = (idx: number) => {
    const comps = [...(form.components ?? [])];
    comps.splice(idx, 1);
    set('components', comps.map((c, i) => ({ ...c, sequence: i + 1 })));
  };

  const handleSave = async () => {
    if (!form.groupCode?.trim() || !form.groupName?.trim()) {
      setError('Group Code and Group Name are required.'); return;
    }
    setSaving(true); setError(null);
    try {
      if (record) { await taxApi.updateGroup(record.id, form); }
      else { await taxApi.createGroup(form); }
      onSaved();
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { message?: string } } }).response?.data?.message ?? 'Save failed.';
      setError(msg);
    } finally { setSaving(false); }
  };

  const inputCls = 'w-full h-9 px-3 rounded-md border text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-brand-500';
  const labelCls = 'block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-2xl mx-4">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-sm font-bold text-gray-900 dark:text-gray-100">{record ? 'Edit Tax Group' : 'New Tax Group'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-lg leading-none">&times;</button>
        </div>
        <div className="p-5 space-y-4 max-h-[80vh] overflow-y-auto">
          {error && <div className="text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 px-3 py-2 rounded">{error}</div>}

          {/* Section 1 — Group details */}
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-gray-400 mb-2">Group Details</h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Group Code *</label>
                <input className={inputCls} value={form.groupCode ?? ''} onChange={e => set('groupCode', e.target.value)} maxLength={30} />
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700 dark:text-gray-300 pb-1">
                  <input type="checkbox" checked={!!form.isActive} onChange={e => set('isActive', e.target.checked)} /> Active
                </label>
              </div>
            </div>
            <div className="mt-3">
              <label className={labelCls}>Group Name *</label>
              <input className={inputCls} value={form.groupName ?? ''} onChange={e => set('groupName', e.target.value)} maxLength={150} />
            </div>
            <div className="mt-3">
              <label className={labelCls}>Remarks</label>
              <textarea className="w-full px-3 py-2 rounded-md border text-sm resize-none bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-brand-500"
                rows={2} value={form.remarks ?? ''} onChange={e => set('remarks', e.target.value || null)} maxLength={500} />
            </div>
          </div>

          {/* Section 2 — Components */}
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-gray-400 mb-2">Tax Components</h3>
            <div className="flex items-center gap-2 mb-3">
              <Combobox className="flex-1" value={addCodeId} onChange={setAddCodeId}
                placeholder="Select tax code to add…"
                options={availableCodes
                  .filter(c => !(form.components ?? []).some(x => x.taxCodeId === c.id))
                  .map(c => ({ value: c.id, label: `${c.code} — ${c.name} (${c.taxRate}%)` }))} />
              <button onClick={addComponent} disabled={!addCodeId}
                className="h-9 px-3 text-xs bg-brand-600 text-white rounded-md disabled:opacity-40 hover:bg-brand-700 flex items-center gap-1">
                <Plus size={13} /> Add
              </button>
            </div>
            {(form.components ?? []).length === 0 ? (
              <p className="text-xs text-slate-400 italic py-2">No components added yet.</p>
            ) : (
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-slate-200 dark:border-slate-700">
                    <th className="text-left py-1.5 px-2 text-slate-500 dark:text-gray-400 font-medium">Code</th>
                    <th className="text-left py-1.5 px-2 text-slate-500 dark:text-gray-400 font-medium">Name</th>
                    <th className="text-left py-1.5 px-2 text-slate-500 dark:text-gray-400 font-medium">Rate</th>
                    <th className="text-left py-1.5 px-2 text-slate-500 dark:text-gray-400 font-medium">Seq</th>
                    <th className="text-left py-1.5 px-2 text-slate-500 dark:text-gray-400 font-medium">On Base</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {(form.components ?? []).map((comp, idx) => (
                    <tr key={comp.taxCodeId} className="border-b border-slate-100 dark:border-slate-800">
                      <td className="py-1.5 px-2 font-mono font-semibold text-brand-700">{comp.code}</td>
                      <td className="py-1.5 px-2 text-slate-700 dark:text-slate-300">{comp.name}</td>
                      <td className="py-1.5 px-2 font-mono text-slate-600 dark:text-gray-400">{comp.taxRate}%</td>
                      <td className="py-1.5 px-2">
                        <input type="number" min="1" className="w-16 h-7 px-2 rounded border text-xs border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
                          value={comp.sequence} onChange={e => updateComponent(idx, 'sequence', parseInt(e.target.value) || 1)} />
                      </td>
                      <td className="py-1.5 px-2 text-center">
                        <input type="checkbox" checked={comp.isOnBase} onChange={e => updateComponent(idx, 'isOnBase', e.target.checked)} />
                      </td>
                      <td className="py-1.5 px-2">
                        <button onClick={() => removeComponent(idx)} className="p-1 rounded hover:bg-red-50 text-red-400 transition-colors"><X size={12} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-gray-200 dark:border-gray-700">
          <button onClick={onClose} className="h-8 px-4 text-xs rounded-md border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800/50">Cancel</button>
          <button onClick={handleSave} disabled={saving}
            className="h-8 px-4 text-xs rounded-md bg-brand-600 hover:bg-brand-700 text-white font-medium disabled:opacity-50 flex items-center gap-1.5">
            {saving && <Spinner size="sm" />} Save
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Page ──────────────────────────────────────────────────────────────────────

export const TaxGroupsPage = () => {
  const { hasPermission } = usePermission();
  const canCreate = hasPermission(Permissions.TAX_GROUP_CREATE as string);
  const canEdit   = hasPermission(Permissions.TAX_GROUP_EDIT as string);
  const canDelete = hasPermission(Permissions.TAX_GROUP_DELETE as string);

  const [items, setItems]   = useState<TaxGroup[]>([]);
  const [total, setTotal]   = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage]     = useState(1);
  const pageSize = 20;

  const [search, setSearch]             = useState('');
  const [dialogOpen, setDialogOpen]     = useState(false);
  const [editRecord, setEditRecord]     = useState<TaxGroup | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<TaxGroup | null>(null);
  const [deleting, setDeleting]         = useState(false);
  const [availableCodes, setAvailableCodes] = useState<TaxCode[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await taxApi.getGroups({ search: search || undefined, pageNumber: page, pageSize });
      const d = res.data?.data;
      setItems(d?.items ?? []);
      setTotal(d?.totalCount ?? 0);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [search, page]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    taxApi.getCodes({ pageSize: 500, isActive: true }).then(r => setAvailableCodes(r.data?.data?.items ?? [])).catch(() => {});
  }, []);

  const handleSaved = () => { setDialogOpen(false); setEditRecord(null); load(); };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try { await taxApi.deleteGroup(deleteTarget.id); setDeleteTarget(null); load(); }
    catch { /* silent */ }
    finally { setDeleting(false); }
  };

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900">
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h1 className="text-xl font-semibold text-slate-900 dark:text-white flex items-center gap-2">
              <Layers size={20} className="text-brand-600" /> Tax Groups
            </h1>
            <p className="text-sm text-slate-500 dark:text-gray-400 mt-0.5">{total.toLocaleString()} records</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 dark:text-gray-400"><RefreshCw size={16} /></button>
            {canCreate && (
              <button onClick={() => { setEditRecord(null); setDialogOpen(true); }}
                className="flex items-center gap-1.5 px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium rounded-lg">
                <Plus size={15} /> New Group
              </button>
            )}
          </div>
        </div>
        <div className="relative max-w-xs">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search code or name…"
            className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-brand-500 outline-none" />
        </div>
      </div>

      <div className="flex-1 overflow-auto">
        {loading ? (
          <div className="flex items-center justify-center h-64"><Spinner size="lg" /></div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-slate-400">
            <Layers size={40} className="mb-3 opacity-40" />
            <p className="text-sm font-medium">No tax groups found</p>
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-slate-50 dark:bg-slate-800/80 backdrop-blur border-b border-slate-200 dark:border-slate-700">
              <tr>
                {['Group Code', 'Group Name', 'Components', 'Status', 'Actions'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {items.map(row => (
                <tr key={row.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors">
                  <td className="px-4 py-2.5 font-mono font-semibold text-brand-700 dark:text-brand-400">{row.groupCode}</td>
                  <td className="px-4 py-2.5 text-slate-800 dark:text-slate-200">{row.groupName}</td>
                  <td className="px-4 py-2.5 text-slate-600 dark:text-slate-400">
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400">
                      {row.components?.length ?? 0} codes
                    </span>
                  </td>
                  <td className="px-4 py-2.5">
                    <span className={cn('px-2 py-0.5 rounded-full text-[10px] font-semibold',
                      row.isActive ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' : 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-gray-400')}>
                      {row.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-1">
                      {canEdit && <button onClick={() => { setEditRecord(row); setDialogOpen(true); }} className="p-1.5 rounded hover:bg-blue-50 text-blue-500"><Edit2 size={13} /></button>}
                      {canDelete && <button onClick={() => setDeleteTarget(row)} className="p-1.5 rounded hover:bg-red-50 text-red-500"><Trash2 size={13} /></button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-6 py-3">
          <p className="text-sm text-slate-500 dark:text-gray-400">Page {page} of {totalPages} · {total.toLocaleString()} records</p>
          <div className="flex items-center gap-1">
            <button onClick={() => setPage(p => p - 1)} disabled={page <= 1} className="p-1.5 rounded hover:bg-slate-100 disabled:opacity-40"><ChevronLeft size={16} /></button>
            <button onClick={() => setPage(p => p + 1)} disabled={page >= totalPages} className="p-1.5 rounded hover:bg-slate-100 disabled:opacity-40"><ChevronRight size={16} /></button>
          </div>
        </div>
      )}

      <GroupDialog open={dialogOpen} record={editRecord} availableCodes={availableCodes}
        onClose={() => { setDialogOpen(false); setEditRecord(null); }} onSaved={handleSaved} />

      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-sm mx-4 p-6">
            <h3 className="text-sm font-bold text-gray-900 dark:text-gray-100 mb-2">Delete Tax Group?</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">Delete <strong>{deleteTarget.groupName}</strong>? This cannot be undone.</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setDeleteTarget(null)} className="h-8 px-4 text-xs rounded-md border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800/50">Cancel</button>
              <button onClick={handleDelete} disabled={deleting}
                className="h-8 px-4 text-xs rounded-md bg-red-600 text-white font-medium disabled:opacity-50 flex items-center gap-1.5">
                {deleting && <Spinner size="sm" />} Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
